import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MetricsCard from './components/MetricsCard';
import TimeToHireChart from './components/TimeToHireChart';
import ConversionFunnelChart from './components/ConversionFunnelChart';
import SourceEffectivenessChart from './components/SourceEffectivenessChart';
import DepartmentMetricsTable from './components/DepartmentMetricsTable';
import FilterPanel from './components/FilterPanel';
import RecommendationsPanel from './components/RecommendationsPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AnalyticsDashboard = () => {
  const [filters, setFilters] = useState({
    dateRange: '90',
    department: 'all',
    jobLevel: 'all',
    hiringManager: 'all',
    compareMode: false,
    comparePeriod: 'previous'
  });

  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  // Enhanced metrics data with more HR-specific KPIs
  const metricsData = [
    {
      title: 'Total Applications',
      value: '2,847',
      change: '+18% vs last month',
      changeType: 'positive',
      icon: 'FileText',
      description: 'Active pipeline health',
      sparklineData: [45, 52, 48, 61, 59, 67, 73, 78, 82, 89, 94, 98]
    },
    {
      title: 'Interviews Scheduled',
      value: '567',
      change: '+24% vs last month',
      changeType: 'positive',
      icon: 'Calendar',
      description: 'Strong screening funnel',
      sparklineData: [23, 28, 31, 35, 42, 48, 52, 58, 61, 65, 69, 74]
    },
    {
      title: 'Successful Hires',
      value: '89',
      change: '+12% vs last month',
      changeType: 'positive',
      icon: 'Users',
      description: 'Quality placement rate',
      sparklineData: [8, 12, 15, 18, 22, 25, 28, 32, 35, 38, 42, 45]
    },
    {
      title: 'Avg. Time-to-Hire',
      value: '23.5 days',
      change: '-4.2 days faster',
      changeType: 'positive',
      icon: 'Clock',
      description: 'Industry: 28.3 days',
      sparklineData: [32, 30, 28, 27, 25, 24, 23, 22, 21, 22, 23, 24]
    },
    {
      title: 'Offer Acceptance Rate',
      value: '87.4%',
      change: '+3.2% vs last month',
      changeType: 'positive',
      icon: 'CheckCircle',
      description: 'Excellent conversion',
      sparklineData: [82, 83, 84, 85, 86, 87, 88, 87, 86, 87, 88, 89]
    },
    {
      title: 'Cost per Hire',
      value: '$3,245',
      change: '-8% vs industry avg',
      changeType: 'positive',
      icon: 'DollarSign',
      description: 'Efficient recruitment',
      sparklineData: [3800, 3700, 3600, 3500, 3400, 3350, 3300, 3275, 3250, 3245, 3240, 3245]
    }
  ];

  const trendData = [
    { metric: 'Applications', value: '+156 this week', trend: 'up', color: 'text-blue-600' },
    { metric: 'Pipeline Velocity', value: '15% faster', trend: 'up', color: 'text-green-600' },
    { metric: 'Source Quality', value: 'LinkedIn leading', trend: 'stable', color: 'text-purple-600' },
    { metric: 'Department Growth', value: 'Engineering +32%', trend: 'up', color: 'text-indigo-600' }
  ];

  const handleFiltersChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    // Simulate export process
    setTimeout(() => {
      setIsExporting(false);
      // In real implementation, this would trigger file download
      console.log(`Exporting analytics report as ${format}`);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          <Breadcrumb />
          
          {/* Page Header with Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-2">
                Analytics Dashboard
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl">
                Transform hiring data into actionable insights with comprehensive metrics and visual analytics
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
                iconName={isExporting ? "Loader2" : "Download"}
                iconPosition="left"
                className="glass border-slate-200/60"
              >
                {isExporting ? 'Exporting...' : 'Export PDF'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('csv')}
                disabled={isExporting}
                iconName="FileSpreadsheet"
                iconPosition="left"
                className="glass border-slate-200/60"
              >
                Export CSV
              </Button>
            </div>
          </div>

          <FilterPanel onFiltersChange={handleFiltersChange} />

          {/* Trends Overview Bar */}
          <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-6 shadow-soft">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">📈 Hiring Trends Overview</h3>
              <div className="text-sm text-slate-500">+15% applications vs last month</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {trendData?.map((trend, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 rounded-xl bg-slate-50/50">
                  <div className={`w-2 h-2 rounded-full ${trend?.color?.replace('text-', 'bg-')}`}></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-700">{trend?.metric}</div>
                    <div className={`text-sm font-semibold ${trend?.color}`}>{trend?.value}</div>
                  </div>
                  <Icon 
                    name={trend?.trend === 'up' ? 'TrendingUp' : 'Minus'} 
                    size={16} 
                    className={trend?.color}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Enhanced KPI Cards with 3-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {metricsData?.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                description={metric?.description}
                sparklineData={metric?.sparklineData}
                className="hover-lift glass-enhanced"
              />
            ))}
          </div>

          {/* Main Charts Section with Visual Analytics */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-6 shadow-soft hover-lift">
              <TimeToHireChart />
            </div>
            <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-6 shadow-soft hover-lift">
              <ConversionFunnelChart />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-6 shadow-soft hover-lift">
              <SourceEffectivenessChart />
            </div>
            <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-6 shadow-soft hover-lift">
              <RecommendationsPanel />
            </div>
          </div>

          {/* Department Performance with Enhanced Visuals */}
          <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-6 shadow-soft">
            <DepartmentMetricsTable />
          </div>

          {/* Executive Summary Insights */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-8 shadow-soft">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-indigo-100 rounded-xl">
                <Icon name="Lightbulb" size={24} className="text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900">Executive Insights & Recommendations</h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-3 p-4 bg-white/60 rounded-xl">
                <div className="flex items-center gap-2">
                  <Icon name="Target" size={18} className="text-green-600" />
                  <h4 className="font-semibold text-slate-800">Top Performance</h4>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Engineering team shows 32% improvement in time-to-hire through streamlined technical interviews and better candidate pre-screening processes.
                </p>
              </div>
              
              <div className="space-y-3 p-4 bg-white/60 rounded-xl">
                <div className="flex items-center gap-2">
                  <Icon name="AlertTriangle" size={18} className="text-amber-600" />
                  <h4 className="font-semibold text-slate-800">Optimization Opportunity</h4>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  LinkedIn sourcing shows high volume (35%) but lower conversion. Consider refining search criteria and initial outreach messaging.
                </p>
              </div>
              
              <div className="space-y-3 p-4 bg-white/60 rounded-xl">
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={18} className="text-blue-600" />
                  <h4 className="font-semibold text-slate-800">Growth Trend</h4>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  87.4% offer acceptance rate indicates strong employer branding and competitive compensation packages across all departments.
                </p>
              </div>
            </div>
          </div>

          {/* Data Freshness Indicator */}
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center space-x-2 text-sm text-slate-500 bg-white/50 px-4 py-2 rounded-full border border-slate-200/60">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Data updated 12 minutes ago • Next refresh in 18 minutes</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;